import React from 'react'
import { useDispatch } from 'react-redux'
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  Badge,
  Alert,
} from 'reactstrap'
import { 
  FaExclamationTriangle, 
  FaComments,
  FaCheck,
  FaExchangeAlt,
  FaFileAlt,
  FaHistory,
  FaFileInvoiceDollar,
  FaTimes
} from 'react-icons/fa'
import { acceptCounterProposal } from '../../store/slices/transportRequestSlice'

const CounterProposal = ({ isOpen, toggle, requestId, proposal }) => {
  const dispatch = useDispatch()

  if (!proposal || !proposal.counter_proposal) {
    return null
  }

  const original = {
    transport_rate: proposal.transport_rate,
    advance_percentage: proposal.advance_percentage,
    payment_terms: proposal.payment_terms,
    additional_charges: proposal.additional_charges || 0,
    total_freight: proposal.total_freight,
    special_terms: proposal.special_terms || '-',
  }

  const counter = proposal.counter_proposal

  const handleAccept = async () => {
    const result = await dispatch(
      acceptCounterProposal({ requestId, proposalId: proposal._id })
    ).unwrap()

    if (result) {
      toggle()
    }
  }

  const renderComparisonRow = (label, originalValue, counterValue) => {
    const isDifferent =
      String(originalValue).trim() !== String(counterValue).trim()
    return (
      <tr>
        <td className="fw-medium">{label}</td>
        <td className={isDifferent ? 'text-muted' : ''}>{originalValue}</td>
        <td className={isDifferent ? 'bg-warning bg-opacity-10 fw-bold' : ''}>
          {counterValue}
        </td>
      </tr>
    )
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" className="aurora-modal">
      <ModalHeader toggle={toggle} className="border-0">
        <span className="neo-text">Counter Protocol Analysis</span>
        <Badge color="warning" className="ms-2 status-pill">
          NEURAL MISMATCH
        </Badge>
      </ModalHeader>
      <ModalBody className="p-4">
        <div className="alert-glass-warning mb-4 d-flex align-items-center">
          <FaExclamationTriangle className="me-3 text-warning" size={24} />
          <div>
            <h6 className="mb-0 fw-bold">Negotiation Protocol Alert</h6>
            <small>Counter-proposal values will be transmitted for manual authorization.</small>
          </div>
        </div>

        <div className="mb-4 glass-effect p-3 rounded-3 border-glass">
          <h6 className="neo-text mb-1">Manifest: {proposal.proposal_number}</h6>
          <small className="text-muted">
            Timestamp: {new Date(counter.counter_date || proposal.updatedAt).toLocaleString()}
          </small>
        </div>

        <div className="glass-effect rounded-3 overflow-hidden border-glass mb-4">
          <Table borderless responsive className="mb-0 custom-table">
            <thead>
              <tr>
                <th style={{ width: '30%' }}>Protocol Key</th>
                <th style={{ width: '35%' }}>Authorized Manifest</th>
                <th style={{ width: '35%' }}>Counter Deviation</th>
              </tr>
            </thead>
            <tbody>
            {renderComparisonRow(
              'Transport Rate',
              `₹ ${original.transport_rate.toFixed(2)}`,
              `₹ ${counter.transport_rate.toFixed(2)}`
            )}
            {renderComparisonRow(
              'Additional Charges',
              `₹ ${original.additional_charges.toFixed(2)}`,
              `₹ ${(counter.additional_charges || 0).toFixed(2)}`
            )}
            {renderComparisonRow(
              'Total Freight',
              `₹ ${original.total_freight.toFixed(2)}`,
              `₹ ${counter.total_freight.toFixed(2)}`
            )}
            {renderComparisonRow(
              'Advance Percentage',
              `${original.advance_percentage}%`,
              `${counter.advance_percentage}%`
            )}
            {renderComparisonRow(
              'Payment Terms',
              original.payment_terms,
              counter.payment_terms
            )}
            {renderComparisonRow(
              'Special Terms',
              original.special_terms,
              counter.special_terms || '-'
            )}
          </tbody>
        </Table>
        </div>

        {counter.counter_notes && (
          <div className="mt-4 p-3 glass-effect rounded-3 border-glass">
            <h6 className="neo-text mb-2">
              <FaComments className="me-2 text-primary" />
              Contractor Annotations
            </h6>
            <p className="mb-0 text-main">{counter.counter_notes}</p>
          </div>
        )}

        <div className="alert-glass-info mt-4 p-3 rounded-3">
          <h6 className="fw-bold mb-2">Operational Protocol:</h6>
          <ul className="mb-0 small">
            <li><strong>Authorize:</strong> Synchronize manifest with counter-terms.</li>
            <li><strong>Revise:</strong> Re-calculate logic and issue V{proposal.version + 1} manifest.</li>
          </ul>
        </div>
      </ModalBody>
      <ModalFooter className="border-0">
        <Button color="secondary" className="glass-effect" onClick={toggle}>
          Abort
        </Button>
        <Button color="primary" className="btn-gradient" onClick={handleAccept}>
          <FaCheck className="me-2" />
          Authorize Counter Protocol
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default CounterProposal
